import { useReducer, useEffect, useCallback } from 'react';
import { useMutation, useApolloClient } from 'react-apollo';
import { toast } from 'react-toastify';
import {
  EDIT_EVENT_BEHAVIOR_OPERATION,
  UPDATE_EVENT_OPERATION
} from 'utils/constants/default';
import { updateCalendarPref } from 'utils/cache/calendar';
import moment from 'moment';
import {
  ADD_EVENT,
  UPDATE_EVENT,
  EDIT_EVENT
} from 'api/graphql/queries/events';
import { HAS_CONVERTED_EVENT_IN_SERIES } from 'api/graphql/v2/queries/Events';
import {
  CONVERTED_EVENT_MESSAGE,
  CONVERTED_SERIES_MESSAGE
} from 'utils/downstreams/DownStreamModalConfigurations';
import { getRefetchQueries, getSubmissionEvent } from './newAppointmentHelpers';
import { useHistory, useParams } from 'react-router';

export const SUBMISSION_STATES = {
  START: 'START_FORM',
  SUBMIT: 'SUBMIT_FORM', // click submit form with valid date
  FETCHING_WARNINGS: 'FETCHING_WARNINGS',
  WARNINGS_FETCHED: 'WARNINGS_FETCHED',
  AUTHORIZATIONS: 'AUTH_WARNINGS',
  CONFLICTS: 'CONFLICTS_WARNINGS',
  SAVE: 'SAVE_APPOINTMENT',
  SAVED: 'APPOINTMENT_SAVED',
  SAVE_FAILED: 'APPOINTMENT_SAVE_FAILED'
};

const DEFAULT_SUBMISSION_STATE = {
  loading: false,
  currentState: SUBMISSION_STATES.START, // current local state of the form
  formData: undefined, // the submitted data from appointment creation
  warnings: undefined, // NO warnings
  conflicts: undefined, // NO conflicts
  warningsVariables: undefined // DATA FOR QUERY TO FETCH WARNINGS
};

export function newAppointmentSubmissionReducer(state, action) {
  switch (action.type) {
    case SUBMISSION_STATES.START:
      return {
        ...DEFAULT_SUBMISSION_STATE
      };
    case SUBMISSION_STATES.SUBMIT:
      // should load warnings and conflicts
      return {
        ...state,
        currentState: SUBMISSION_STATES.SUBMIT,
        loading: true,
        ...action.payload
      }; // payload is the data to submit
    case SUBMISSION_STATES.AUTHORIZATIONS:
      return {
        ...state,
        currentState: SUBMISSION_STATES.AUTHORIZATIONS,
        loading: false,
        ...action.payload
      };
    case SUBMISSION_STATES.CONFLICTS:
      return {
        ...state,
        currentState: SUBMISSION_STATES.CONFLICTS,
        loading: false,
        ...action.payload
      };
    case SUBMISSION_STATES.SAVE:
      return {
        ...state,
        loading: true,
        currentState: SUBMISSION_STATES.SAVE,
        ...action.payload
      };
    case SUBMISSION_STATES.SAVED:
      return {
        ...state,
        currentState: SUBMISSION_STATES.SAVED,
        loading: false
      };
    default:
      console.log('unknown state triggered', action);
      return state;
  }
}

export const useSubmissionReducer = (
  apptTypes,
  eventTypes,
  client,
  clinics,
  oldId,
  eventMeta
) => {
  const { operation } = useParams();
  const history = useHistory();
  const [state, dispatch] = useReducer(
    newAppointmentSubmissionReducer,
    DEFAULT_SUBMISSION_STATE
  );

  const [addEvent] = useMutation(ADD_EVENT);
  const [updateEvent] = useMutation(UPDATE_EVENT);
  const [editEvent] = useMutation(EDIT_EVENT);
  const apolloClient = useApolloClient();

  const saveAppointment = useCallback(
    async data => {
      // Shared converted-event guard for both the reschedule (updateEvent) and
      // edit-behavior (editEvent) paths. "Update the selected series" expands to
      // this-and-following on the backend; "Only this event" checks just this
      // instance. Returns true (and blocks) when a converted event is found.
      const isSeriesUpdate = !!eventMeta.thisAndFollowingEvents;
      const isBlockedByConversion = async () => {
        try {
          const { data: seriesData } = await apolloClient.query({
            query: HAS_CONVERTED_EVENT_IN_SERIES,
            variables: { ids: [oldId], recurrent: isSeriesUpdate },
            fetchPolicy: 'network-only'
          });
          if (seriesData?.hasConvertedEventInSeries) {
            toast.error(
              isSeriesUpdate
                ? CONVERTED_SERIES_MESSAGE
                : CONVERTED_EVENT_MESSAGE
            );
            dispatch({ type: SUBMISSION_STATES.START });
            return true;
          }
        } catch (error) {
          // If the check fails we fall through and let the save proceed,
          // surfacing any real error through the mutation's catch below.
        }
        return false;
      };

      if (!oldId)
        addEvent({
          variables: {
            event: getSubmissionEvent(
              data,
              client,
              apptTypes,
              data.clinicId,
              eventTypes,
              clinics
            )
          },
          refetchQueries: getRefetchQueries(data.selectedSlot)
        })
          .then(() => dispatch({ type: SUBMISSION_STATES.SAVED }))
          .catch(error => dispatch({ type: SUBMISSION_STATES.START }));
      else if (operation === UPDATE_EVENT_OPERATION) {
        if (await isBlockedByConversion()) return;
        updateEvent({
          variables: {
            id: oldId,
            recurrent: eventMeta.thisAndFollowingEvents,
            event: getSubmissionEvent(
              data,
              client,
              apptTypes,
              data.clinicId,
              eventTypes,
              clinics
            ),
            cancelNote: eventMeta.cancelNote,
            cancelReasonId: eventMeta.cancelReasonId
          },
          refetchQueries: getRefetchQueries(data.selectedSlot)
        })
          .then(() => dispatch({ type: SUBMISSION_STATES.SAVED }))
          .catch(error => dispatch({ type: SUBMISSION_STATES.START }));
      } else if (operation === EDIT_EVENT_BEHAVIOR_OPERATION) {
        if (await isBlockedByConversion()) return;
        editEvent({
          variables: {
            id: oldId,
            recurrent: eventMeta.thisAndFollowingEvents,
            event: getSubmissionEvent(
              data,
              client,
              apptTypes,
              data.clinicId,
              eventTypes,
              clinics
            )
          },
          refetchQueries: getRefetchQueries(data.selectedSlot)
        })
          .then(() => dispatch({ type: SUBMISSION_STATES.SAVED }))
          .catch(error => dispatch({ type: SUBMISSION_STATES.START }));
      }
    },
    [
      oldId,
      addEvent,
      updateEvent,
      editEvent,
      apptTypes,
      eventTypes,
      client,
      eventMeta,
      clinics,
      operation,
      dispatch,
      apolloClient
    ]
  );

  useEffect(() => {
    if (state.currentState === SUBMISSION_STATES.SAVE) {
      saveAppointment(state.formData);
    } else if (state.currentState === SUBMISSION_STATES.SAVED) {
      updateCalendarPref(moment(state.formData.selectedDate));
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return [state, dispatch];
};
